import React from 'react';

export type StoryLane = {
  id: string;
  label: string;
  summary: string;
  detail: string;
};

export type StoryBlock = {
  eyebrow: string;
  title: string;
  lead: string;
  lanesTitle: string;
  lanes: StoryLane[];
  architectureNote: string;
};

export function RestructuringStoryCore({ story }: { story: StoryBlock }) {
  return (
    <section className="mt-14">
      <div className="relative overflow-hidden border border-mist-200 bg-gradient-to-br from-ink-900 via-ink-900 to-navy-900 px-6 py-10 text-mist-50 md:px-10 md:py-14">
        <div
          className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-navy-700/30"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-navy-400/10"
          aria-hidden
        />
        <p className="relative text-xs font-semibold uppercase tracking-[0.22em] text-navy-400">
          {story.eyebrow}
        </p>
        <h2 className="relative mt-4 max-w-2xl font-display text-3xl text-mist-50 md:text-4xl">
          {story.title}
        </h2>
        {story.lead && (
          <p className="relative mt-5 max-w-2xl text-base leading-relaxed text-mist-200 md:text-lg">
            {story.lead}
          </p>
        )}
      </div>

      {story.lanes.length > 0 && (
        <div className="mt-12">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-navy-700">
            {story.lanesTitle}
          </p>
          <ol className="mt-8 grid gap-10 md:grid-cols-3 md:gap-8">
            {story.lanes.map((lane, i) => (
              <li
                key={lane.id}
                className="animate-fade-up border-t border-navy-700/40 pt-5"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <p className="font-display text-sm text-navy-700">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="mt-3 font-display text-xl text-ink-900">
                  {lane.label}
                </h3>
                {lane.summary && (
                  <p className="mt-2 text-sm font-medium leading-snug text-ink-900">
                    {lane.summary}
                  </p>
                )}
                {lane.detail && (
                  <p className="mt-3 text-sm leading-relaxed text-ink-700">
                    {lane.detail}
                  </p>
                )}
              </li>
            ))}
          </ol>
          {story.architectureNote && (
            <p className="mt-10 max-w-3xl border-l-2 border-navy-400 pl-4 text-sm leading-relaxed text-ink-700">
              {story.architectureNote}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
